import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for handling text selection and comment positioning
 */
export const useTextSelection = (onSelectionChange) => {
  const [selection, setSelection] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionPosition, setSelectionPosition] = useState(null);
  const selectionTimeoutRef = useRef(null);

  /**
   * Get the text content and position of a DOM element
   */
  const getElementTextInfo = useCallback((element, blockIndex) => {
    if (!element) return null;

    const text = element.textContent || element.innerText || '';
    const rect = element.getBoundingClientRect();
    
    return {
      text,
      rect,
      blockIndex,
      element
    };
  }, []);

  /**
   * Calculate character offsets within a text node
   */
  const getCharacterOffsets = useCallback((range, textInfo) => {
    if (!range || !textInfo) return null;

    const { text } = textInfo;
    const selectedText = range.toString();
    
    // Find the start and end positions of the selection within the text
    let startOffset = 0;
    let endOffset = text.length;

    // Create a temporary range to measure character positions
    const tempRange = range.cloneRange();
    const preCaretRange = range.cloneRange();
    
    try {
      // Get the start offset
      preCaretRange.selectNodeContents(textInfo.element);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      startOffset = preCaretRange.toString().length;

      // Get the end offset
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      endOffset = preCaretRange.toString().length;
    } catch (error) {
      console.warn('Error calculating character offsets:', error);
      // Fallback: use indexOf for simple cases
      const startIndex = text.indexOf(selectedText);
      if (startIndex !== -1) {
        startOffset = startIndex;
        endOffset = startIndex + selectedText.length;
      }
    }

    return {
      startOffset,
      endOffset,
      selectedText
    };
  }, []);

  /**
   * Handle text selection events
   */
  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    
    if (!sel || sel.rangeCount === 0) {
      setSelection(null);
      setSelectionPosition(null);
      setIsSelecting(false);
      return;
    }

    const selectedText = sel.toString().trim();
    
    if (selectedText.length === 0) {
      setSelection(null);
      setSelectionPosition(null);
      setIsSelecting(false);
      return;
    }

    const range = sel.getRangeAt(0);
    const commonAncestor = range.commonAncestorContainer;
    
    // Find the nearest block element (paragraph, heading, etc.)
    let blockElement = commonAncestor.nodeType === Node.TEXT_NODE 
      ? commonAncestor.parentElement 
      : commonAncestor;

    // Traverse up to find the actual content block
    while (blockElement && !blockElement.hasAttribute('data-block-index')) {
      blockElement = blockElement.parentElement;
    }

    if (!blockElement) {
      console.warn('Could not find block element for selection');
      return;
    }

    const blockIndex = parseInt(blockElement.getAttribute('data-block-index'));
    const textInfo = getElementTextInfo(blockElement, blockIndex);
    const offsets = getCharacterOffsets(range, textInfo);

    if (!offsets) {
      console.warn('Could not calculate character offsets');
      return;
    }

    const newSelection = {
      text: selectedText,
      blockIndex,
      startOffset: offsets.startOffset,
      endOffset: offsets.endOffset,
      range: range.cloneRange(),
      element: blockElement,
      rect: range.getBoundingClientRect()
    };

    setSelection(newSelection);
    setSelectionPosition({
      top: range.getBoundingClientRect().top + window.scrollY,
      left: range.getBoundingClientRect().left + window.scrollX,
      width: range.getBoundingClientRect().width,
      height: range.getBoundingClientRect().height
    });
    setIsSelecting(true);

    // Notify parent component
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }

    // Clear any existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Set a longer timeout to allow user to click the comment button
    selectionTimeoutRef.current = setTimeout(() => {
      setIsSelecting(false);
    }, 10000); // Increased to 10 seconds to give user time to interact
  }, [onSelectionChange, getElementTextInfo, getCharacterOffsets]);

  /**
   * Clear the current selection
   */
  const clearSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
    }
    setSelection(null);
    setSelectionPosition(null);
    setIsSelecting(false);
    
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }
  }, []);

  /**
   * Check if a point is within the selection area
   */
  const isPointInSelection = useCallback((x, y) => {
    if (!selectionPosition) return false;
    
    const { left, top, width, height } = selectionPosition;
    return x >= left && x <= left + width && y >= top && y <= top + height;
  }, [selectionPosition]);

  /**
   * Get selection data for comment creation
   */
  const getSelectionData = useCallback(() => {
    if (!selection) return null;

    return {
      blockIndex: selection.blockIndex,
      startOffset: selection.startOffset,
      endOffset: selection.endOffset,
      highlightedText: selection.text
    };
  }, [selection]);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionChange);
    document.addEventListener('keyup', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionChange);
      document.removeEventListener('keyup', handleSelectionChange);
      
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleSelectionChange]);

  return {
    selection,
    selectionPosition,
    isSelecting,
    clearSelection,
    isPointInSelection,
    getSelectionData
  };
};
